<script setup lang="ts">
import { computed } from 'vue'
import type { IWidget } from '@/interfaces/components/dashboard/widget'
import type { WidgetDensity } from '@/composables/useCustomDashboardWidget'
import { useTotalizerWidget } from '@/composables/widgets/useTotalizerWidget'
import { formatDashboardMinutesFromApi } from '@/utils/date/date-time'
import { useI18n } from '@/composable/useI18n'

const props = defineProps<{
  widget: IWidget
  density: WidgetDensity
}>()

const { t } = useI18n()
const { getState, isLoading, hasError } = useTotalizerWidget()

const state = computed(() => getState(props.widget))
const dataField = computed(() => props.widget.config.data_field || '')

const valueClass = computed(() => {
  if (props.density === 'dense') return 'font-satoshi text-lg font-bold leading-none text-black'
  if (props.density === 'compact') return 'font-satoshi text-2xl font-bold leading-none text-black'
  return 'font-satoshi text-3xl font-bold leading-none text-black'
})

const unitClass = computed(() => {
  if (props.density === 'dense') return 'text-xs font-medium text-body'
  if (props.density === 'compact') return 'text-sm font-medium text-body'
  return 'text-lg font-medium text-body'
})

const labelClass = computed(() => {
  if (props.density === 'dense') return 'mt-0.5 text-3xs font-medium tracking-wide text-blue'
  if (props.density === 'compact') return 'mt-1 text-3xs font-medium tracking-wide text-blue'
  return 'mt-1 text-2xs font-medium tracking-wide text-blue'
})

const containerSpacing = computed(() => {
  if (props.density === 'dense') return 'space-y-2'
  if (props.density === 'compact') return 'space-y-3'
  return 'space-y-4'
})

interface FormattedValue {
  number: string
  unit: string
}

function formatDashboardLocaleNumber(
  value: number | undefined | null,
  minimumFractionDigits: number,
  maximumFractionDigits: number,
): string {
  if (value == null || Number.isNaN(Number(value))) return '—'
  return Number(value).toLocaleString('pt-BR', { minimumFractionDigits, maximumFractionDigits })
}

function formatKw(value: number | null | undefined): FormattedValue {
  const number = formatDashboardLocaleNumber(value, 1, 1)
  return { number, unit: 'kW' }
}

function formatKwh(value: number | null | undefined): FormattedValue {
  const number = formatDashboardLocaleNumber(value, 1, 1)
  return { number, unit: 'kWh' }
}

function formatMinutes(value: number | null | undefined): FormattedValue {
  if (value == null || Number.isNaN(Number(value))) {
    return { number: '—', unit: '' }
  }
  
  const formatted = formatDashboardMinutesFromApi(Number(value))
  // Para tempo formatado (ex: "233h 26min"), mantemos tudo como número sem unidade separada
  // pois o formato já inclui as unidades h/min integradas
  return { number: formatted, unit: '' }
}

function formatCycleCount(value: number | null | undefined): FormattedValue {
  const number = formatDashboardLocaleNumber(value, 0, 0)
  return { number, unit: '' }
}

const displayData = computed(() => {
  if (isLoading(props.widget)) {
    return { items: [{ number: '...', unit: '', label: '' }], loading: true }
  }

  if (hasError(props.widget) || !state.value) {
    return { items: [{ number: '—', unit: '', label: '' }], loading: false }
  }

  const field = dataField.value

  if (field === 'demand') {
    const peak = formatKw(state.value.demand?.peak_demand_kw)
    const offPeak = formatKw(state.value.demand?.off_peak_demand_kw)
    return {
      items: [
        {
          number: peak.number,
          unit: peak.unit,
          label: t('custom_dashboards.totalizer_labels.peak_demand'),
        },
        {
          number: offPeak.number,
          unit: offPeak.unit,
          label: t('custom_dashboards.totalizer_labels.off_peak_demand'),
        },
      ],
      loading: false,
    }
  }

  if (field === 'utilization') {
    const totalUtil = formatMinutes(state.value.utilization?.total_utilization_time)
    const totalNonUtil = formatMinutes(state.value.utilization?.total_non_utilization_time)
    return {
      items: [
        {
          number: totalUtil.number,
          unit: totalUtil.unit,
          label: t('custom_dashboards.totalizer_labels.total_utilization_time'),
        },
        {
          number: totalNonUtil.number,
          unit: totalNonUtil.unit,
          label: t('custom_dashboards.totalizer_labels.total_non_utilization_time'),
        },
      ],
      loading: false,
    }
  }

  if (field === 'consumption') {
    const total = formatKwh(state.value.consumption?.total_consumption_kwh)
    const peak = formatKwh(state.value.consumption?.peak_consumption_kwh)
    const offPeak = formatKwh(state.value.consumption?.off_peak_consumption_kwh)
    return {
      items: [
        {
          number: total.number,
          unit: total.unit,
          label: t('custom_dashboards.totalizer_labels.total_consumption'),
        },
        {
          number: peak.number,
          unit: peak.unit,
          label: t('custom_dashboards.totalizer_labels.peak_consumption'),
        },
        {
          number: offPeak.number,
          unit: offPeak.unit,
          label: t('custom_dashboards.totalizer_labels.off_peak_consumption'),
        },
      ],
      loading: false,
    }
  }

  if (field === 'mixingCycle') {
    const count = formatCycleCount(state.value.mixing_cycle?.cycle_count)
    const avgTime = formatMinutes(state.value.mixing_cycle?.average_time)
    const totalTime = formatMinutes(state.value.mixing_cycle?.total_time)
    
    return {
      items: [
        {
          number: count.number,
          unit: count.unit,
          label: t('custom_dashboards.totalizer_labels.mixing_cycle_count'),
        },
        {
          number: avgTime.number,
          unit: avgTime.unit,
          label: t('custom_dashboards.totalizer_labels.mixing_average_time'),
        },
        {
          number: totalTime.number,
          unit: totalTime.unit,
          label: t('custom_dashboards.totalizer_labels.mixing_total_time'),
        },
      ],
      loading: false,
    }
  }

  return { items: [{ number: '—', unit: '', label: '' }], loading: false }
})
</script>

<template>
  <div class="flex flex-col items-center justify-center" :class="containerSpacing">
    <div
      v-for="(item, index) in displayData.items"
      :key="index"
      class="flex flex-col items-center"
    >
      <div class="flex min-w-0 items-baseline justify-center gap-1">
        <span :class="valueClass">{{ item.number }}</span>
        <span v-if="item.unit" :class="unitClass">{{ item.unit }}</span>
      </div>
      <p v-if="item.label" :class="labelClass">
        {{ item.label }}
      </p>
    </div>
  </div>
</template>
